import { useState, useEffect } from 'react'
import apiClient from '../../lib/api'

interface AutomationSettingsProps {
  projectId: number
  projectName: string
  onUpdate?: () => void
}

interface AutomationConfig {
  automation_level: 'manual' | 'review' | 'full_auto'
  automation_paused: boolean
  automation_config: {
    max_posts_per_subreddit_per_day: number
    auto_mode_daily_threshold: number
    review_mode_daily_threshold: number
    consecutive_error_threshold: number
    quality_score_minimum: number
    notification_preferences: {
      email_on_approval_needed: boolean
      email_on_error: boolean
      daily_digest: boolean
    }
  }
}

export default function AutomationSettings({ projectId, projectName, onUpdate }: AutomationSettingsProps) {
  const [config, setConfig] = useState<AutomationConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    loadConfig()
  }, [projectId])

  const loadConfig = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('🔍 Loading automation config for project:', projectId)
      const response = await apiClient.getAutomationConfig(projectId)

      console.log('📥 Load response:', response)

      if (response.success) {
        // Ensure all nested properties exist with defaults
        const configData = response.data
        console.log('📊 Raw config data from server:', configData)

        const safeConfig: AutomationConfig = {
          automation_level: configData.automation_level || 'manual',
          automation_paused: configData.automation_paused || false,
          automation_config: {
            max_posts_per_subreddit_per_day: configData.automation_config?.max_posts_per_subreddit_per_day || 5,
            auto_mode_daily_threshold: configData.automation_config?.auto_mode_daily_threshold || 10,
            review_mode_daily_threshold: configData.automation_config?.review_mode_daily_threshold || 20,
            consecutive_error_threshold: configData.automation_config?.consecutive_error_threshold || 3,
            quality_score_minimum: configData.automation_config?.quality_score_minimum || 70,
            notification_preferences: {
              email_on_approval_needed: configData.automation_config?.notification_preferences?.email_on_approval_needed || false,
              email_on_error: configData.automation_config?.notification_preferences?.email_on_error || false,
              daily_digest: configData.automation_config?.notification_preferences?.daily_digest || false,
            }
          }
        }

        console.log('✅ Loaded and processed config:', safeConfig)
        setConfig(safeConfig)
      } else {
        console.error('❌ Failed to load config:', response)
        setError('Failed to load automation settings')
      }
    } catch (err: any) {
      console.error('💥 Error loading automation config:', err)
      setError(err.message || 'Failed to load automation settings')
    } finally {
      setLoading(false)
    }
  }

  const handleLevelChange = async (level: 'manual' | 'review' | 'full_auto') => {
    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      const response = await apiClient.toggleAutomation(projectId, level)

      if (response.success) {
        setConfig(prev => prev ? {
          ...prev,
          automation_level: level,
          automation_paused: response.data.automation_paused
        } : null)
        setSuccessMessage(`Automation mode changed to ${level}`)

        if (onUpdate) onUpdate()
      } else {
        setError('Failed to update automation level')
      }
    } catch (err: any) {
      console.error('Error updating automation level:', err)
      setError(err.message || 'Failed to update automation level')
    } finally {
      setSaving(false)
    }
  }

  const handleConfigUpdate = async () => {
    if (!config) return

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      console.log('💾 Saving automation config for project:', projectId)
      console.log('📤 Config being sent:', config.automation_config)

      const response = await apiClient.updateAutomationConfig(projectId, config.automation_config)

      console.log('📥 Save response:', response)

      if (response.success) {
        setSuccessMessage('Configuration saved successfully')

        // Reload config from server to confirm it was saved
        console.log('🔄 Reloading config from server...')
        await loadConfig()

        if (onUpdate) onUpdate()
      } else {
        console.error('❌ Save failed:', response)
        setError('Failed to save configuration')
      }
    } catch (err: any) {
      console.error('💥 Error saving config:', err)
      setError(err.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handlePauseToggle = async () => {
    if (!config) return

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      // Toggle by setting to manual then back, or just toggle paused state
      const newLevel = config.automation_paused ? config.automation_level : 'manual'
      const response = await apiClient.toggleAutomation(projectId, newLevel)

      if (response.success) {
        setConfig(prev => prev ? {
          ...prev,
          automation_paused: !config.automation_paused
        } : null)
        setSuccessMessage(config.automation_paused ? 'Automation resumed' : 'Automation paused')

        if (onUpdate) onUpdate()
      }
    } catch (err: any) {
      console.error('Error toggling pause:', err)
      setError(err.message || 'Failed to toggle pause state')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-red-600">Failed to load automation settings</div>
        <button
          onClick={loadConfig}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          🤖 Automation Settings
        </h3>
        <p className="text-sm text-gray-600">
          Configure how {projectName} handles opportunity responses automatically
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* Automation Level */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Automation Mode
        </label>

        <div className="space-y-3">
          {/* Manual */}
          <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
            config.automation_level === 'manual'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="automation_level"
              value="manual"
              checked={config.automation_level === 'manual'}
              onChange={() => handleLevelChange('manual')}
              disabled={saving}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                ✋ Manual - No Automation
              </div>
              <div className="text-sm text-gray-600 mt-1">
                You'll manually generate and post all comments. Full control, no automation.
              </div>
            </div>
          </label>

          {/* Review */}
          <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
            config.automation_level === 'review'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="automation_level"
              value="review"
              checked={config.automation_level === 'review'}
              onChange={() => handleLevelChange('review')}
              disabled={saving}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                👀 Review - Auto-Generate, Manual Approval
              </div>
              <div className="text-sm text-gray-600 mt-1">
                System auto-generates comments, but you approve before posting. Safe and efficient.
              </div>
            </div>
          </label>

          {/* Full Auto */}
          <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
            config.automation_level === 'full_auto'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="automation_level"
              value="full_auto"
              checked={config.automation_level === 'full_auto'}
              onChange={() => handleLevelChange('full_auto')}
              disabled={saving}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                🚀 Full Auto - Generate & Post Automatically
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Fully automated. System generates and schedules comments automatically. Maximum efficiency.
              </div>
              <div className="text-xs text-orange-600 mt-1 font-medium">
                ⚠️ Will auto-downgrade to Review mode at {config.automation_config.auto_mode_daily_threshold} posts/day
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Pause/Resume */}
      {config.automation_level !== 'manual' && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">
                {config.automation_paused ? '⏸️ Automation Paused' : '▶️ Automation Active'}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {config.automation_paused
                  ? 'Automation is temporarily paused. No comments will be generated.'
                  : 'Automation is running. New opportunities will be processed automatically.'}
              </div>
            </div>
            <button
              onClick={handlePauseToggle}
              disabled={saving}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                config.automation_paused
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              } disabled:opacity-50`}
            >
              {config.automation_paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>
      )}

      {/* Advanced Configuration */}
      {config.automation_level !== 'manual' && (
        <div className="space-y-6">
          <div className="border-t pt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">⚙️ Advanced Settings</h4>

            {/* Safety Limits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Posts per Subreddit/Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={config.automation_config.max_posts_per_subreddit_per_day}
                  onChange={(e) => setConfig({
                    ...config,
                    automation_config: {
                      ...config.automation_config,
                      max_posts_per_subreddit_per_day: parseInt(e.target.value) || 2
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Safety cap to prevent spam detection (recommended: 2)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality Score Minimum
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  value={config.automation_config.quality_score_minimum}
                  onChange={(e) => setConfig({
                    ...config,
                    automation_config: {
                      ...config.automation_config,
                      quality_score_minimum: parseFloat(e.target.value) || 7
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only auto-post opportunities above this score
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto→Review Threshold
                </label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={config.automation_config.auto_mode_daily_threshold}
                  onChange={(e) => setConfig({
                    ...config,
                    automation_config: {
                      ...config.automation_config,
                      auto_mode_daily_threshold: parseInt(e.target.value) || 15
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-downgrade full_auto to review at X posts/day
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review→Manual Threshold
                </label>
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={config.automation_config.review_mode_daily_threshold}
                  onChange={(e) => setConfig({
                    ...config,
                    automation_config: {
                      ...config.automation_config,
                      review_mode_daily_threshold: parseInt(e.target.value) || 30
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-downgrade review to manual at X posts/day
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Error Auto-Pause Threshold
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={config.automation_config.consecutive_error_threshold}
                  onChange={(e) => setConfig({
                    ...config,
                    automation_config: {
                      ...config.automation_config,
                      consecutive_error_threshold: parseInt(e.target.value) || 3
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-pause after X consecutive errors
                </p>
              </div>
            </div>

            {/* Email Notifications */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                📧 Email Notifications
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.automation_config.notification_preferences.email_on_approval_needed}
                    onChange={(e) => setConfig({
                      ...config,
                      automation_config: {
                        ...config.automation_config,
                        notification_preferences: {
                          ...config.automation_config.notification_preferences,
                          email_on_approval_needed: e.target.checked
                        }
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Email when comment needs approval (review mode)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.automation_config.notification_preferences.email_on_error}
                    onChange={(e) => setConfig({
                      ...config,
                      automation_config: {
                        ...config.automation_config,
                        notification_preferences: {
                          ...config.automation_config.notification_preferences,
                          email_on_error: e.target.checked
                        }
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Email on automation errors or auto-pause
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.automation_config.notification_preferences.daily_digest}
                    onChange={(e) => setConfig({
                      ...config,
                      automation_config: {
                        ...config.automation_config,
                        notification_preferences: {
                          ...config.automation_config.notification_preferences,
                          daily_digest: e.target.checked
                        }
                      }
                    })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    Daily automation summary email (9 AM)
                  </span>
                </label>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleConfigUpdate}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-sm text-blue-900">
          <div className="font-semibold mb-2">💡 How Automation Works:</div>
          <ul className="list-disc list-inside space-y-1 text-blue-800">
            <li><strong>Manual:</strong> You control everything - generate and post comments yourself</li>
            <li><strong>Review:</strong> System generates comments automatically, you approve before posting</li>
            <li><strong>Full Auto:</strong> System generates and schedules comments automatically (with safety limits)</li>
          </ul>
          <div className="mt-3 text-xs text-blue-700">
            <strong>Safety features:</strong> Per-subreddit daily caps, graduated escalation thresholds,
            error auto-pause, and integration with existing anti-spam protection ensure natural Reddit behavior.
          </div>
        </div>
      </div>
    </div>
  )
}
